/**
 * MediaLibraryPicker — "use the logo we already have".
 *
 * Opens over whatever field asked for it, searches everything already uploaded,
 * and hands back the chosen file's URL. Filtered to the kinds that field will
 * accept, so a video never appears where a favicon is wanted.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, ImageOff } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { searchLibrary, prettySize } from '@/lib/mediaLibrary';
import { AssetThumb } from '@/components/media/AssetThumb';

const KINDS_FOR = {
  logo: ['image'], favicon: ['image'], banner: ['image'], cover: ['image'],
  avatar: ['image'], photo: ['image'], product: ['image'],
  hero: ['image', 'video'], gallery: ['image', 'video'],
  video: ['video'], audio: ['audio'], document: ['document', 'image'],
  general: ['image', 'video', 'audio', 'document'],
};

export const MediaLibraryPicker = ({ open, onOpenChange, purpose = 'general', onPick }) => {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const kinds = useMemo(() => KINDS_FOR[purpose] || KINDS_FOR.general, [purpose]);
  const [kind, setKind] = useState(kinds[0]);

  useEffect(() => { setKind(kinds[0]); }, [kinds]);
  useEffect(() => {
    const id = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ['media-library-pick', term, kind],
    queryFn: () => searchLibrary({ q: term, kind, per_page: 60 }),
    enabled: open,
  });

  const assets = data?.assets || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="media-picker-dialog">
        <DialogHeader>
          <DialogTitle>Use something already uploaded</DialogTitle>
          <DialogDescription>
            Picking a file here points this field at the same copy — change it
            once in the Media Library and it changes everywhere.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by name" className="pl-8"
                   data-testid="media-picker-search" />
          </div>
          {kinds.length > 1 && kinds.map((k) => (
            <Button key={k} type="button" size="sm"
                    variant={kind === k ? 'default' : 'outline'}
                    onClick={() => setKind(k)}
                    data-testid={`media-picker-kind-${k}`}>
              {k}
            </Button>
          ))}
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          {isLoading ? (
            <p className="py-12 text-center text-sm text-slate-500">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Looking…
            </p>
          ) : assets.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-500" data-testid="media-picker-empty">
              <ImageOff className="mx-auto mb-2 h-6 w-6 text-slate-300" />
              Nothing matching has been uploaded yet.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-5">
              {assets.map((a) => (
                <button key={a.id} type="button"
                        onClick={() => { onPick(a); onOpenChange(false); }}
                        className="group overflow-hidden rounded-lg border border-slate-200
                                   text-left transition-all hover:border-slate-900
                                   hover:shadow-md focus:outline-none focus:ring-2
                                   focus:ring-slate-900"
                        data-testid={`media-picker-asset-${a.id}`}>
                  <div className="aspect-square bg-slate-50">
                    <AssetThumb asset={a} />
                  </div>
                  <div className="p-2">
                    <p className="truncate text-[11px] font-medium text-slate-700">{a.name}</p>
                    <p className="text-[10px] text-slate-400">{prettySize(a.size)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibraryPicker;
